import React, { useEffect } from 'react';
import i18next from 'i18next';
import {
  getFormattedDateString,
  getFormattedDateTimeString,
  getFormattedDateYearString,
  getFormattedMonthAndDayString,
} from 'lib/DateTime';

/**
 * Utility function to get formatted date and time values.
 * @param {string} dateString - The date string to be formatted.
 * @param {string} timeZone - The time zone for date formatting.
 * @param {string} delimiter - Delimiter to separate date and time.
 * @param {boolean} dateOnly - Flag to indicate if only the date should be displayed.
 * @returns {object} - An object containing formatted date and time values.
 */
export const getFormattedValues = (dateString, timeZone, delimiter, dateOnly) => {
  const formattedDateString = getFormattedDateString(dateString, timeZone, {}, i18next);
  const formattedDateTime = getFormattedDateTimeString(dateString, timeZone, delimiter, i18next);
  const monthAndDay = getFormattedMonthAndDayString(dateString, timeZone, i18next);
  const year = getFormattedDateYearString(dateString, timeZone);

  return {
    timestamp: dateOnly ? formattedDateString : formattedDateTime,
    monthAndDay,
    year,
  };
};

/**
 * Timestamp component to display formatted date and time.
 *
 * @param {object} props - Component properties.
 * @param {boolean} [props.dateOnly] - Flag to indicate if only the date should be displayed.
 * @param {string} [props.dateString] - The date string to be formatted.
 * @param {string} [props.delimiter] - Delimiter to separate date and time.
 * @param {string} [props.layout] - Layout type for the timestamp display.
 * @param {string} [props.timeZone] - Time zone for date formatting.
 */
function Timestamp(props) {
  const {
    dateOnly = false,
    dateString = '',
    delimiter = ' / ',
    layout,
    timeZone,
  } = props;

  const fallbackValues = getFormattedValues(dateString, 'America/New_York', delimiter, dateOnly);

  const [timestampToRender, setTimestampToRender] = React.useState(
    fallbackValues.timestamp,
  );
  const [formattedMonthAndDay, setFormattedMonthAndDay] = React.useState(
    fallbackValues.monthAndDay,
  );
  const [formattedYear, setFormattedYear] = React.useState(fallbackValues.year);

  useEffect(() => {
    const { timestamp, monthAndDay, year } = getFormattedValues(
      dateString,
      timeZone,
      delimiter,
      dateOnly,
    );

    setTimestampToRender(timestamp);
    setFormattedMonthAndDay(monthAndDay);
    setFormattedYear(year);
  }, [dateString, timeZone, dateOnly, delimiter]);

  if (dateString === '') {
    return null;
  }

  return (
    <>
      {layout === 'split' ? (
        <>
          <span className="dib mr1 ws-nowrap" data-test="timestamp__monthDay" data-testid="timestamp__monthDay">
            {formattedMonthAndDay}
            {delimiter}
          </span>
          <span className="dib ws-nowrap" data-test="timestamp__year">
            {formattedYear}
          </span>
        </>
      ) : (
        timestampToRender
      )}
    </>
  );
}

export default Timestamp;
